/**
 * Behavioral Workbench
 *
 * 3-panel layout for applying behavioral codes to document chunks.
 * - Left Panel: Code Browser (categories, search, shortcuts)
 * - Center Panel: Document Viewer (chunks with applied codes)
 * - Right Panel: Coding Panel (tabs for coding, intelligence, entities)
 */

import React, { useState, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
  Snackbar
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useEntityContext } from '../EntityContextSwitch';
import { useAuth } from '../../contexts/AuthContext';

// Hooks
import { useWorkbenchState } from './hooks/useWorkbenchState';
import { useKeyboardShortcuts, buildShortcutMap } from './hooks/useKeyboardShortcuts';

// Components
import CodeBrowser from './components/LeftPanel/CodeBrowser';
import DocumentViewer from './components/CenterPanel/DocumentViewer';
import CodingPanel from './components/RightPanel/CodingPanel';
import KeyboardShortcutsModal from './components/Modals/KeyboardShortcutsModal';
import NewCodeModal from './components/Modals/NewCodeModal';
import StatusBar from './components/StatusBar/StatusBar';

// Constants
import { PANEL_WIDTHS } from './constants/workbenchConfig';

// eslint-disable-next-line max-lines-per-function
function BehavioralWorkbench() {
  const { currentEntity, loading: entityLoading } = useEntityContext();
  const { isPlatformAdmin } = useAuth();

  // Main state hook
  const workbench = useWorkbenchState(currentEntity?.id);

  // UI state
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showNewCode, setShowNewCode] = useState(false);
  const [editingCodeData, setEditingCodeData] = useState(null);
  const [leftPanelWidth, _setLeftPanelWidth] = useState(PANEL_WIDTHS.left);
  const [rightPanelWidth, _setRightPanelWidth] = useState(PANEL_WIDTHS.right);

  // Snackbar state for toast notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Refs for focus management
  const searchInputRef = useRef(null);

  // Build shortcut map for keyboard handler
  const codesByShortcut = useMemo(
    () => buildShortcutMap(workbench.codes),
    [workbench.codes]
  );

  // Handle code application from any source
  const handleApplyCode = useCallback(
    async (codeName, options = {}) => {
      try {
        await workbench.applyCode(codeName, options);
        setSnackbar({ open: true, message: `Code "${codeName}" applied`, severity: 'success' });
      } catch (error) {
        console.error('Failed to apply code:', error);
        setSnackbar({ open: true, message: `Failed to apply code: ${error.message}`, severity: 'error' });
      }
    },
    [workbench]
  );

  // Handle code removal with toast feedback
  const handleRemoveCode = useCallback(
    async (chunkIdOrCodeName, codeName) => {
      try {
        await workbench.removeCode(chunkIdOrCodeName, codeName);
        const displayName = codeName || chunkIdOrCodeName;
        setSnackbar({ open: true, message: `Code "${displayName}" removed`, severity: 'info' });
      } catch (error) {
        console.error('Failed to remove code:', error);
        setSnackbar({ open: true, message: `Failed to remove code: ${error.message}`, severity: 'error' });
      }
    },
    [workbench]
  );

  // Handle AI suggestion acceptance with toast feedback
  const handleAcceptSuggestion = useCallback(
    async (suggestion) => {
      try {
        await workbench.acceptSuggestion(suggestion);
        setSnackbar({ open: true, message: `AI suggestion "${suggestion.code}" accepted`, severity: 'success' });
      } catch (error) {
        console.error('Failed to accept suggestion:', error);
        setSnackbar({ open: true, message: `Failed to accept suggestion: ${error.message}`, severity: 'error' });
      }
    },
    [workbench]
  );

  // Handle accept all suggestions with toast feedback
  const handleAcceptAllSuggestions = useCallback(
    async () => {
      try {
        await workbench.acceptAllSuggestions();
        setSnackbar({ open: true, message: 'All AI suggestions accepted', severity: 'success' });
      } catch (error) {
        console.error('Failed to accept all suggestions:', error);
        setSnackbar({ open: true, message: `Failed to accept suggestions: ${error.message}`, severity: 'error' });
      }
    },
    [workbench]
  );

  // Code management handlers
  const handleNewCode = useCallback(() => {
    setEditingCodeData(null);
    setShowNewCode(true);
  }, []);

  const handleEditCode = useCallback((code) => {
    setEditingCodeData(code);
    setShowNewCode(true);
  }, []);

  const handleCodeSaved = useCallback(() => {
    workbench.refreshCodes();
    setSnackbar({ open: true, message: editingCodeData ? 'Code updated' : 'Code created', severity: 'success' });
  }, [workbench, editingCodeData]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onNextChunk: workbench.nextChunk,
    onPrevChunk: workbench.prevChunk,
    onNextDocument: workbench.nextDocument,
    onPrevDocument: workbench.prevDocument,
    onApplyCode: handleApplyCode,
    onShowShortcuts: () => setShowShortcuts(true),
    onFocusSearch: () => searchInputRef.current?.focus(),
    onEscape: () => {
      setShowShortcuts(false);
      searchInputRef.current?.blur();
    },
    // AI suggestion shortcuts
    onAcceptSuggestion: handleAcceptSuggestion,
    onRejectSuggestion: workbench.rejectSuggestion,
    onFetchAISuggestions: workbench.fetchAISuggestions,
    aiSuggestions: workbench.aiSuggestions,
    codesByShortcut,
    enabled: true,
  });

  // Loading state
  if (entityLoading || workbench.codesLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (workbench.codesError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load behavioral codes: {workbench.codesError}
        </Alert>
      </Box>
    );
  }

  // No entity selected
  if (!currentEntity?.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Please select an entity from the header to access the Behavioral Workbench.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)', // Subtract header height
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Behavioral Workbench
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentEntity.name}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={workbench.learningMode}
                onChange={(e) => workbench.setLearningMode(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Learning Mode
              </Typography>
            }
          />

          <Tooltip title="Auto-fetch AI suggestions when selecting chunks">
            <FormControlLabel
              control={
                <Switch
                  checked={workbench.aiAutoFetch}
                  onChange={(e) => workbench.setAiAutoFetch(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 14, color: '#0097B2' }} />
                  <Typography variant="body2" color="text.secondary">
                    Auto AI
                  </Typography>
                </Box>
              }
            />
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Tooltip title="Focus code search (Ctrl+F)">
            <IconButton size="small" onClick={() => searchInputRef.current?.focus()}>
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Keyboard shortcuts (?)">
            <IconButton size="small" onClick={() => setShowShortcuts(true)}>
              <KeyboardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Main 3-Panel Layout */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
        }}
      >
        {/* Left Panel: Code Browser */}
        <Box
          sx={{
            width: leftPanelWidth,
            minWidth: PANEL_WIDTHS.minLeft,
            maxWidth: PANEL_WIDTHS.maxLeft,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          <CodeBrowser
            codes={workbench.codes}
            codesByDimension={workbench.filteredCodesByDimension}
            expandedCategories={workbench.expandedCategories}
            onToggleCategory={workbench.toggleCategory}
            searchTerm={workbench.searchTerm}
            onSearchChange={workbench.setSearchTerm}
            searchInputRef={searchInputRef}
            onCodeClick={(code) => handleApplyCode(code.code)}
            onNewCode={handleNewCode}
            onEditCode={handleEditCode}
            learningMode={workbench.learningMode}
            selectedChunk={workbench.currentChunk}
            isAdmin={isPlatformAdmin}
          />
        </Box>

        {/* Center Panel: Document Viewer */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'background.default',
          }}
        >
          <DocumentViewer
            documents={workbench.documents}
            currentDocIndex={workbench.currentDocIndex}
            onSelectDocument={workbench.selectDocument}
            onNextDocument={workbench.nextDocument}
            onPrevDocument={workbench.prevDocument}
            chunks={workbench.chunks}
            selectedChunkIndex={workbench.selectedChunkIndex}
            onSelectChunk={workbench.selectChunk}
            progress={workbench.documentProgress}
            loading={workbench.chunksLoading || workbench.documentsLoading}
            error={workbench.documentsError}
            onRemoveCode={handleRemoveCode}
            aiSuggestions={workbench.aiSuggestions}
            onAcceptSuggestion={handleAcceptSuggestion}
          />
        </Box>

        {/* Right Panel: Coding Panel */}
        <Box
          sx={{
            width: rightPanelWidth,
            minWidth: PANEL_WIDTHS.minRight,
            maxWidth: PANEL_WIDTHS.maxRight,
            borderLeft: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          <CodingPanel
            codes={workbench.codes}
            chunks={workbench.chunks}
            currentChunk={workbench.currentChunk}
            onApplyCode={handleApplyCode}
            onRemoveCode={handleRemoveCode}
            onUpdateCode={workbench.updateCode}
            learningMode={workbench.learningMode}
            documentProgress={workbench.documentProgress}
            // AI suggestion props
            aiSuggestions={workbench.aiSuggestions}
            aiLoading={workbench.aiLoading}
            aiError={workbench.aiError}
            processingAiAction={workbench.processingAiAction}
            onFetchAISuggestions={workbench.fetchAISuggestions}
            onAcceptSuggestion={handleAcceptSuggestion}
            onRejectSuggestion={workbench.rejectSuggestion}
            onAcceptAllSuggestions={handleAcceptAllSuggestions}
            // Entity scoring props
            entityId={currentEntity?.id}
            entityName={currentEntity?.name}
            // AI fetch state
            hasFetched={workbench.aiHasFetched}
          />
        </Box>
      </Box>

      {/* Status Bar */}
      <StatusBar
        currentChunkIndex={workbench.selectedChunkIndex}
        totalChunks={workbench.chunks.length}
        progress={workbench.documentProgress}
      />

      {/* Modals */}
      <KeyboardShortcutsModal
        open={showShortcuts}
        onClose={() => setShowShortcuts(false)}
        codesByShortcut={codesByShortcut}
      />

      {isPlatformAdmin && (
        <NewCodeModal
          open={showNewCode}
          onClose={() => {
            setShowNewCode(false);
            setEditingCodeData(null);
          }}
          onSaved={handleCodeSaved}
          editCode={editingCodeData}
        />
      )}

      {/* Toast Notifications — bottom-left to avoid overlapping StatusBar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ mb: 4 }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', fontSize: '0.8rem' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default BehavioralWorkbench;
